import { Helmet } from "react-helmet-async";
import styled, { css } from "styled-components";
import Grid from "assets/homepage/landing/Grid.svg";
import { typography } from "./components/typography";
import { TopSectionContainer } from "./styles";
import { hexToRgba } from "utils/ColorUtil";

const NotFoundPage: React.FC = () => {
  return (
    <Container>
      <Helmet>
        <title>404 • Beam</title>
      </Helmet>
      <MainInfo>
        <Title>404</Title>
        <SubTitle>There's nothing here!</SubTitle>
      </MainInfo>
    </Container>
  );
};

export default NotFoundPage;

const Container = styled(TopSectionContainer)`
  ${({ theme }) => {
    return css`
      position: relative;
      z-index: 0;
      justify-content: center;
      height: 100%;

      background-image: url(${Grid}),
        linear-gradient(
          180deg,
          rgba(23, 24, 28, 1) 0%,
          rgba(23, 24, 28, 0) 33.33%,
          rgba(23, 24, 28, 0) 66.67%,
          rgba(23, 24, 28, 1) 100%
        ),
        linear-gradient(
          90deg,
          rgba(23, 24, 28, 0) 0%,
          rgba(23, 24, 28, 0.3) 33.33%,
          rgba(23, 24, 28, 0.8) 66.67%,
          rgba(23, 24, 28, 1) 100%
        ),
        radial-gradient(
          50% 50% at 0% 50%,
          ${hexToRgba(theme.colors.blue9, 0.8)} 0%,
          ${hexToRgba(theme.colors.blue9, 0)} 100%
        ),
        conic-gradient(
          from 0deg at 0% 50%,
          ${hexToRgba(theme.colors.blue9, 0)} 0deg,
          ${hexToRgba(theme.colors.blue9, 0.5)} 90deg,
          ${hexToRgba(theme.colors.blue9, 0.8)} 180deg,
          ${hexToRgba(theme.colors.blue9, 0)} 360deg
        ),
        conic-gradient(
          from 0deg at 0% 50%,
          ${hexToRgba(theme.colors.blue9, 0.8)} 0deg,
          ${hexToRgba(theme.colors.blue9, 0.5)} 90deg,
          ${hexToRgba(theme.colors.blue9, 0)} 180deg,
          ${hexToRgba(theme.colors.blue9, 0)} 360deg
        ),
        linear-gradient(
          180deg,
          rgba(23, 24, 28, 1) 0%,
          rgba(23, 24, 28, 0) 33.33%,
          rgba(23, 24, 28, 0) 66.67%,
          rgba(23, 24, 28, 1) 100%
        ),
        linear-gradient(
          90deg,
          rgba(23, 24, 28, 1) 0%,
          rgba(23, 24, 28, 0.8) 33.33%,
          rgba(23, 24, 28, 0.3) 66.67%,
          rgba(23, 24, 28, 0) 100%
        ),
        radial-gradient(
          50% 50% at 100% 50%,
          ${hexToRgba(theme.colors.violet9, 0.8)} 0%,
          ${hexToRgba(theme.colors.violet9, 0)} 100%
        ),
        conic-gradient(
          from 0deg at 100% 50%,
          ${hexToRgba(theme.colors.violet9, 0)} 0deg,
          ${hexToRgba(theme.colors.violet9, 0)} 90deg,
          ${hexToRgba(theme.colors.violet9, 0.8)} 180deg,
          ${hexToRgba(theme.colors.violet9, 0.5)} 270deg,
          ${hexToRgba(theme.colors.violet9, 0)} 360deg
        ),
        conic-gradient(
          from 0deg at 100% 50%,
          ${hexToRgba(theme.colors.violet9, 0.8)} 0deg,
          ${hexToRgba(theme.colors.violet9, 0)} 90deg,
          ${hexToRgba(theme.colors.violet9, 0)} 180deg,
          ${hexToRgba(theme.colors.violet9, 0.5)} 270deg,
          ${hexToRgba(theme.colors.violet9, 0.8)} 360deg
        );

      --bg-img-max-width: 40%;
      --bg-img-max-height: 300px;
      --bg-img-radial-max-width: 90px;
      --bg-img-radial-max-height: 120px;

      @media (min-width: ${theme.breakpoints.sm}px) {
        --bg-img-max-width: 240px;
        --bg-img-max-height: 320px;
        --bg-img-radial-max-width: 90px;
        --bg-img-radial-max-height: 120px;
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        --bg-img-max-width: 240px;
        --bg-img-max-height: 320px;
        --bg-img-radial-max-width: 180px;
        --bg-img-radial-max-height: 120px;
      }

      @media (min-width: ${theme.breakpoints.lg}px) {
        --bg-img-max-width: 320px;
        --bg-img-max-height: 360px;
        --bg-img-radial-max-width: 240px;
        --bg-img-radial-max-height: 120px;
      }

      @media (min-width: ${theme.breakpoints.xl}px) {
        --bg-img-max-width: 480px;
        --bg-img-max-height: 400px;
        --bg-img-radial-max-width: 320px;
        --bg-img-radial-max-height: 120px;
      }

      background-size: cover, var(--bg-img-max-width) var(--bg-img-max-height),
        var(--bg-img-max-width) var(--bg-img-max-height),
        var(--bg-img-radial-max-width) var(--bg-img-radial-max-height),
        var(--bg-img-max-width) calc(var(--bg-img-max-height) / 2),
        var(--bg-img-max-width) calc(var(--bg-img-max-height) / 2),
        var(--bg-img-max-width) var(--bg-img-max-height), var(--bg-img-max-width) var(--bg-img-max-height),
        var(--bg-img-radial-max-width) var(--bg-img-radial-max-height),
        var(--bg-img-max-width) calc(var(--bg-img-max-height) / 2),
        var(--bg-img-max-width) calc(var(--bg-img-max-height) / 2);
      background-position: center top, top left, top left,
        top calc(var(--bg-img-max-height) / 2 - var(--bg-img-radial-max-height) / 2) left, top left,
        top calc(var(--bg-img-max-height) / 2) left, top right, top right,
        top calc(var(--bg-img-max-height) / 2 - var(--bg-img-radial-max-height) / 2) right, top right,
        top calc(var(--bg-img-max-height) / 2) right;
      background-repeat: no-repeat;
    `;
  }}
`;

export const MainInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0px;
`;

export const Title = styled.h1`
  ${({ theme }) => {
    return css`
      all: unset;
      margin-bottom: 20px;
      color: ${theme.colors.gray12};
      max-width: 100%;

      ${typography.title36};

      @media (min-width: ${theme.breakpoints.sm}px) {
        ${typography.title44};
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        ${typography.title48};
      }

      @media (min-width: ${theme.breakpoints.lg}px) {
        ${typography.title56};
      }

      @media (min-width: ${theme.breakpoints.xl}px) {
        ${typography.title64};
      }
    `;
  }}
`;

const SubTitle = styled.p`
  ${({ theme }) => {
    return css`
      all: unset;
      margin-bottom: 48px;
      color: ${theme.colors.gray11};
      max-width: 560px;

      ${typography.textLgLight};

      @media (min-width: ${theme.breakpoints.sm}px) {
        ${typography.textXlLight};
      }
    `;
  }}
`;
